import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { listWorkspaceDistribution } from '../../../services';
import { validateApiResponse } from '../../../shared/utils/apiResponse';
import { useAbortableLoad } from './useAbortableLoad.js';

/**
 * Org workspace distribution list (CT-4209). Race/unmount-safe via
 * {@link useAbortableLoad}; the page owns search/sort/pagination.
 */
export function useWorkspacesDistribution() {
  const { t } = useTranslation('admin-studio');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const run = useAbortableLoad();

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    await run(async (isStale) => {
      try {
        const res = await listWorkspaceDistribution();
        if (isStale()) return;
        setRows(validateApiResponse(res, t('common.requestFailed')).body ?? []);
      } catch (err) {
        if (isStale()) return;
        setError(err?.message ?? t('common.requestFailed'));
      } finally {
        if (!isStale()) setIsLoading(false);
      }
    });
  }, [t, run]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(
    () => ({ rows, isLoading, error, refetch: load }),
    [rows, isLoading, error, load],
  );
}
